import {
  ApiError,
  CABINET_CODE_ROTATION_DAYS,
  type CabinetPickup,
  type CabinetScanResult,
  type CabinetSnapshotEvent,
  cabinetCodeSchema,
  queryKeys,
  type UpdateVendingMachineInput,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Camera, Eye, EyeOff, Lock, ScanLine } from 'lucide-react'
import { useState } from 'react'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const cabinetKeys = {
  pickups: (machineId: string) => ['vending', 'machines', machineId, 'pickups'] as const,
}

const PICKUP_TONE: Record<CabinetPickup['status'], NonNullable<BadgeProps['tone']>> = {
  open: 'info',
  confirmed: 'success',
  timed_out: 'danger',
  cancelled: 'neutral',
}

const PICKUP_LABEL: Record<CabinetPickup['status'], string> = {
  open: 'Collecting',
  confirmed: 'Collected',
  timed_out: 'Unfinished',
  cancelled: 'Cancelled',
}

const SCAN_LABEL: Record<CabinetScanResult, string> = {
  matched: 'Matched',
  not_in_cart: 'Not on order',
  already_scanned: 'Extra',
  unknown_code: 'Unrecognized',
}

const SNAPSHOT_LABEL: Record<CabinetSnapshotEvent, string> = {
  code_shown: 'Code shown',
  scan: 'Scan',
  mismatch: 'Wrong item scanned',
  lock_confirmed: 'Said locked',
  lock_refused: 'Said not locked',
  timed_out: 'Timed out',
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

// ------------------------------------------------------------- settings

function CodeCard({ machine }: { machine: VendingMachine }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [revealed, setRevealed] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const age = daysSince(machine.cabinetCodeChangedAt)

  const save = useMutation({
    mutationFn: (patch: UpdateVendingMachineInput) =>
      api.patch<VendingMachine>(`/vending/machines/${machine.id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machine(machine.id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machines() })
    },
    onError: (err) =>
      toast.error('Could not save', err instanceof ApiError ? err.message : undefined),
  })

  function submitCode() {
    const parsed = cabinetCodeSchema.safeParse(draft)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid code')
      return
    }
    setError(null)
    save.mutate(
      { cabinetCode: parsed.data },
      {
        onSuccess: () => {
          setDraft('')
          toast.success(
            'Cabinet code saved',
            'Customers see the new code from their next purchase. Set the same code on the lock now.',
          )
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="size-4" /> Cabinet code
        </CardTitle>
        <CardDescription>
          Shown to a customer only after their payment goes through, and only until they finish
          scanning. Anyone who has bought from this cabinet knows the current code, so change it on
          the lock and here regularly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {machine.cabinetCode ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="rounded-lg bg-surface-sunken px-4 py-2 font-identifier text-2xl font-semibold tracking-[0.3em]">
              {revealed ? machine.cabinetCode : '•'.repeat(machine.cabinetCode.length)}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? 'Hide code' : 'Show code'}
            >
              {revealed ? <EyeOff /> : <Eye />} {revealed ? 'Hide' : 'Show'}
            </Button>
            {age !== null ? (
              // A code every past customer knows gets riskier the longer it stays;
              // staff get a reminder issue once it passes the rotation window.
              <Badge tone={age >= CABINET_CODE_ROTATION_DAYS ? 'warning' : 'neutral'}>
                Changed {age === 0 ? 'today' : `${age} day${age === 1 ? '' : 's'} ago`}
                {age >= CABINET_CODE_ROTATION_DAYS ? ' — due for a change' : ''}
              </Badge>
            ) : null}
          </div>
        ) : (
          <Alert tone="warning">
            No code is set, so the tablet is showing an out-of-service screen and taking no
            payments.
          </Alert>
        )}

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            submitCode()
          }}
        >
          <Field label={machine.cabinetCode ? 'New code' : 'Code'} error={error ?? undefined}>
            {(props) => (
              <Input
                {...props}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
                className="w-40 font-identifier tracking-widest"
                placeholder="e.g. 4471"
              />
            )}
          </Field>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!draft.trim()}>
            {machine.cabinetCode ? 'Change code' : 'Set code'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function CheckoutCard({ machine }: { machine: VendingMachine }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [timeout, setTimeoutValue] = useState(String(machine.cabinetPickupTimeoutMinutes))

  const save = useMutation({
    mutationFn: (patch: UpdateVendingMachineInput) =>
      api.patch<VendingMachine>(`/vending/machines/${machine.id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machine(machine.id) })
      toast.success('Saved')
    },
    onError: (err) =>
      toast.error('Could not save', err instanceof ApiError ? err.message : undefined),
  })

  const minutes = Number(timeout)
  const timeoutValid = Number.isInteger(minutes) && minutes >= 2 && minutes <= 60

  return (
    <Card>
      <CardHeader>
        <CardTitle>Checkout</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-md border border-border p-2.5">
          <div>
            <p className="text-sm">Let customers type a card number</p>
            <p className="text-xs text-foreground-muted">
              For cards that can't tap. Typed-in cards cost more per sale and carry more fraud risk
              than a tap.
            </p>
          </div>
          <Switch
            checked={machine.cardEntryFallback}
            disabled={save.isPending}
            onCheckedChange={(checked) => save.mutate({ cardEntryFallback: checked })}
          />
        </div>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (timeoutValid) save.mutate({ cabinetPickupTimeoutMinutes: minutes })
          }}
        >
          <Field
            label="Minutes to scan before staff are alerted"
            error={timeoutValid ? undefined : 'Between 2 and 60 minutes'}
          >
            {(props) => (
              <Input
                {...props}
                value={timeout}
                onChange={(e) => setTimeoutValue(e.target.value)}
                inputMode="numeric"
                className="w-24"
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="secondary"
            disabled={!timeoutValid || minutes === machine.cabinetPickupTimeoutMinutes}
            loading={save.isPending}
          >
            Save
          </Button>
        </form>
        {!machine.alertPhone && !machine.alertEmail ? (
          <Alert tone="info">
            No alert phone or email is set for this machine, so unfinished pickups only show up
            under Issues.
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function CabinetSettingsTab({ machine }: { machine: VendingMachine }) {
  const { can } = useAuth()
  if (!can('kiosks:manage')) {
    return <Alert tone="info">Changing the cabinet code needs the Manage kiosks permission.</Alert>
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="lg:col-span-2">
        <Alert tone="info">
          The tablet sells whatever is in stock at <strong>{machine.locationName}</strong>. Restock
          the cabinet by transferring stock or phones to that location; phones must be sellable
          (filtered and verified) to appear.
        </Alert>
      </div>
      <CodeCard machine={machine} />
      <CheckoutCard machine={machine} />
    </div>
  )
}

// -------------------------------------------------------------- pickups

function SnapshotDialog({
  machineId,
  snapshot,
  onClose,
}: {
  machineId: string
  snapshot: CabinetPickup['snapshots'][number] | null
  onClose: () => void
}) {
  return (
    <Dialog open={snapshot !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {snapshot ? SNAPSHOT_LABEL[snapshot.event] : ''}
            {snapshot?.cameraName ? ` · ${snapshot.cameraName}` : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-2">
          {snapshot ? (
            // Loaded only when someone asks to see it — every load of this
            // URL is a recorded view in the audit log.
            <img
              src={`${apiBaseUrl}/vending/machines/${machineId}/snapshots/${snapshot.id}`}
              alt={`Camera still: ${SNAPSHOT_LABEL[snapshot.event]}`}
              className="w-full rounded-md bg-surface-sunken"
            />
          ) : null}
          <p className="text-xs text-foreground-muted">
            {snapshot ? formatTime(snapshot.capturedAt) : ''} · Viewing a still is recorded in the
            audit log.
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function PickupRow({
  machineId,
  pickup,
  canView,
  onView,
}: {
  machineId: string
  pickup: CabinetPickup
  canView: boolean
  onView: (snapshot: CabinetPickup['snapshots'][number]) => void
}) {
  const [open, setOpen] = useState(pickup.status === 'timed_out' || pickup.paymentState === 'held')
  const toast = useToast()
  const queryClient = useQueryClient()
  const settle = useMutation({
    mutationFn: (action: 'capture' | 'release') =>
      api.post(`/vending/machines/${machineId}/pickups/${pickup.id}/${action}`, {}),
    onSuccess: (_, action) => {
      void queryClient.invalidateQueries({ queryKey: cabinetKeys.pickups(machineId) })
      toast.success(action === 'capture' ? 'The card was charged.' : 'The card hold was released.')
    },
    onError: (err) =>
      toast.error('That did not work', err instanceof ApiError ? err.message : undefined),
  })
  const refused = pickup.scans.filter((s) => s.result !== 'matched').length

  return (
    <li className="py-3">
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-start"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Badge tone={PICKUP_TONE[pickup.status]}>{PICKUP_LABEL[pickup.status]}</Badge>
        <span className="font-identifier text-sm">{pickup.orderNumber}</span>
        <span className="text-sm text-foreground-muted">{money(pickup.totalCents)}</span>
        {pickup.buyerName ? <span className="text-sm">{pickup.buyerName}</span> : null}
        {refused > 0 ? (
          <Badge tone="warning">
            {refused} refused scan{refused === 1 ? '' : 's'}
          </Badge>
        ) : null}
        {pickup.lockState === 'not_locked' ? <Badge tone="danger">Not locked</Badge> : null}
        {pickup.paymentState === 'held' ? <Badge tone="warning">Card on hold</Badge> : null}
        {pickup.paymentState === 'released' ? <Badge tone="neutral">Hold released</Badge> : null}
        <span className="ms-auto text-xs text-foreground-muted">{formatTime(pickup.openedAt)}</span>
      </button>

      {open ? (
        <div className="mt-3 grid gap-4 border-s-2 border-border ps-4 md:grid-cols-2">
          <div>
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
              <ScanLine className="size-3.5" /> Scans
            </p>
            {pickup.scans.length === 0 ? (
              <p className="text-sm text-foreground-muted">Nothing scanned.</p>
            ) : (
              <ol className="space-y-1">
                {pickup.scans.map((scan, i) => (
                  <li key={i} className="flex items-baseline gap-2 text-sm">
                    <span className="w-16 shrink-0 text-xs text-foreground-subtle">
                      {new Date(scan.createdAt).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </span>
                    <Badge tone={scan.result === 'matched' ? 'success' : 'warning'}>
                      {SCAN_LABEL[scan.result]}
                    </Badge>
                    <span className="min-w-0 truncate">
                      {scan.productName ?? <span className="font-identifier">{scan.code}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div>
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
              <Camera className="size-3.5" /> Camera stills
            </p>
            {pickup.snapshots.length === 0 ? (
              <p className="text-sm text-foreground-muted">No stills for this pickup.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {pickup.snapshots.map((snapshot) => (
                  <li key={snapshot.id}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!canView}
                      onClick={() => onView(snapshot)}
                    >
                      {SNAPSHOT_LABEL[snapshot.event]}
                      {snapshot.cameraName ? ` · ${snapshot.cameraName}` : ''}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {pickup.paymentState === 'held' && canView ? (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-foreground-muted">
                  The customer's card is on hold for {money(pickup.totalCents)}. Nothing has been
                  charged. Check the camera stills, then charge it or let it go.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={settle.isPending}
                    onClick={() => {
                      if (window.confirm(`Charge ${money(pickup.totalCents)} to the held card?`)) {
                        settle.mutate('capture')
                      }
                    }}
                  >
                    Charge card
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={settle.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Release the hold? The customer is not charged and the order is cancelled.',
                        )
                      ) {
                        settle.mutate('release')
                      }
                    }}
                  >
                    Release hold
                  </Button>
                </div>
              </div>
            ) : null}
            <Link
              to="/orders/$orderId"
              params={{ orderId: pickup.orderId }}
              className="mt-3 inline-block text-sm text-primary hover:underline"
            >
              Open order
            </Link>
          </div>
        </div>
      ) : null}
    </li>
  )
}

export function PickupsTab({ machineId }: { machineId: string }) {
  const { can } = useAuth()
  const [viewing, setViewing] = useState<CabinetPickup['snapshots'][number] | null>(null)
  const { data: pickups, isLoading } = useQuery({
    queryKey: cabinetKeys.pickups(machineId),
    queryFn: () => api.get<CabinetPickup[]>(`/vending/machines/${machineId}/pickups`),
    refetchInterval: 15_000,
  })

  if (isLoading) {
    return (
      <Card>
        <CardContent className="h-32 pt-4" />
      </Card>
    )
  }
  if (!pickups?.length) {
    return (
      <Card>
        <EmptyState
          icon={ScanLine}
          title="No pickups yet"
          description="Each paid order collected from this cabinet shows up here with its scans and camera stills."
        />
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="pt-2">
        <ul className="divide-y divide-border">
          {pickups.map((pickup) => (
            <PickupRow
              key={pickup.id}
              machineId={machineId}
              pickup={pickup}
              canView={can('kiosks:manage')}
              onView={setViewing}
            />
          ))}
        </ul>
      </CardContent>
      <SnapshotDialog machineId={machineId} snapshot={viewing} onClose={() => setViewing(null)} />
    </Card>
  )
}
